import { useEffect, useRef } from "react";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { fetchSEMOdds } from "@/features/betslip/services/semPricing";
import { legSetKey } from "@/features/betslip/utils/rules";

// Keeps one SEM's server-priced odds in sync with its legs. Mounted per SEM by
// OutcomeSubscriptions, not by a card: a SEM drawn only as a multi leg, or
// sitting in a collapsed section, still has to re-price when its legs change.
export const useSemOdds = (outcomeId: string) => {
  const token = useUserToken();
  const setSemOdds = useBetslipStore((state) => state.setSemOdds);
  const setSemRedundantLegs = useBetslipStore(
    (state) => state.setSemRedundantLegs,
  );

  // Order-independent fingerprint of the legs. Selecting the primitive key
  // (not the legs array) keeps the effect from firing on unrelated slip
  // updates — it re-runs only when these legs actually change.
  const legKey = useBetslipStore((state) => {
    const selection = state.selections[outcomeId];
    if (!selection || selection.kind !== "sem") return "";
    return legSetKey(selection.legs.map((leg) => leg.outcomeId));
  });

  const lastKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!token || !legKey) return;

    const previousKey = lastKeyRef.current;
    lastKeyRef.current = legKey;

    // First sight: the SEM arrives already priced, so seed only.
    if (previousKey === undefined) return;
    // Legs unchanged (e.g. the effect re-ran after a token refresh).
    if (previousKey === legKey) return;

    const selection = useBetslipStore.getState().selections[outcomeId];
    if (!selection || selection.kind !== "sem") return;
    const legOutcomeIds = selection.legs.map((leg) => leg.outcomeId);

    let ignore = false;
    const reprice = async () => {
      const result = await fetchSEMOdds({ outcomeIds: legOutcomeIds, token });
      if (ignore || !result.ok) return;
      setSemOdds(outcomeId, result.data.odds);
      setSemRedundantLegs(outcomeId, result.data.redundantOutcomeIds);
    };
    void reprice();

    // Drop an in-flight response if the legs change again or the card unmounts.
    return () => {
      ignore = true;
    };
  }, [legKey, token, outcomeId, setSemOdds, setSemRedundantLegs]);
};
